import { useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import List from '@mui/icons-material/List';
import ExitToApp from '@mui/icons-material/ExitToApp';
import AccessAlarm from '@mui/icons-material/AccessAlarm';
import AccountCircle from '@mui/icons-material/AccountCircle';
import InsertDriveFile from '@mui/icons-material/InsertDriveFile';
import Schedule from '@mui/icons-material/Schedule';
import BookOpenPageVariant from 'mdi-material-ui/BookOpenPageVariant';
import CalendarMonth from 'mdi-material-ui/CalendarMonth';
import FileEditOutline from 'mdi-material-ui/FileEditOutline';
import OndemandVideo from '@mui/icons-material/OndemandVideo';
import { ContactSupport } from '@mui/icons-material';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import queryString from 'query-string';
import { IndicatorCount } from 'components';
import { getLifebooksChild } from 'redux/selectors';
import { getRouteRoot, usePasswordConfirm, errors } from 'helpers';
import { useCreateDispatches } from 'helpers/hooks';
import { logoutUser, initializeUser, getUserFromToken } from 'services';
import styles from '../lifebookNav.module.css';
import { getUserWithVerify } from 'services/user-services';
import { setSnackbarReadOnly } from 'redux/reducers/snackbarReducer';

export default function useNavOptions(root) {
	const { childId } = useParams();
	const { push } = useHistory();
	const { search, pathname } = useLocation();
	const { setLoader, setSnackbarError } = useCreateDispatches();
	const [pass, setPass] = useState();
	const dispatch = useDispatch();

	const { token } = queryString.parse(search);
	const tokenQueryString = token ? `?token=${token}` : '';

	const isVault = window.location.href.includes('vault');
	const timelineLabel = isVault ? 'Posts' : 'LifeBook';
	const routeRoot = getRouteRoot(root, pathname);
	const vaultRouteRoot = getRouteRoot('vault', pathname);
	const calendarRouteRoot = getRouteRoot('calendar', pathname);
	const calendarActivityRouteRoot = getRouteRoot(
		'calendarActivity',
		pathname
	);

	const { pendingPostCount } = useSelector(getLifebooksChild);

	const getExitReadOnlyAttempts = () => {
		const eroa = localStorage.getItem('eroa');
		return Number(atob(eroa));
	};

	const setExitReadOnlyAttempts = (val) =>
		localStorage.setItem('eroa', btoa([val]));

	const onReadOnlyPasswordConfirm = usePasswordConfirm({
		onFailure: () => {
			setSnackbarError(errors.READ_ONLY_PASSWORD);
			logoutUser();
		},
		maxAttempts: 3,
		getAttempts: getExitReadOnlyAttempts,
		setAttempts: setExitReadOnlyAttempts,
	});

	const navOptions = {
		profile: {
			label: 'Profile',
			onClick: () => {
				const path =
					root === 'lifebooks'
						? 'lifebooks/manage'
						: `${routeRoot}/child`;
				push(`/${path}/${childId}${tokenQueryString}`);
			},
			icon: <AccountCircle />,
		},
		timeline: {
			label: timelineLabel,
			onClick: () => {
				push(`/${routeRoot}/timeline/${childId}${tokenQueryString}`);
			},
			icon: <BookOpenPageVariant />,
		},
		activity: {
			label: 'Activity',
			onClick: () => {
				push(`/${routeRoot}/activity/${childId}${tokenQueryString}`);
			},
			icon: <List />,
		},
		drafts: {
			label: 'Drafts',
			onClick: () => {
				push(`/${routeRoot}/drafts/${childId}${tokenQueryString}`);
			},
			icon: <FileEditOutline />,
		},
		services: {
			label: 'Services',
			onClick: () => {
				push(`/${routeRoot}/services/${childId}${tokenQueryString}`);
			},
			icon: <AccessAlarm />,
		},
		pending: {
			label: 'Pending',
			onClick: () => {
				push(`/${routeRoot}/pending/${childId}${tokenQueryString}`);
			},
			icon: <Schedule />,
			indicator: pendingPostCount && (
				<IndicatorCount className={styles.lifebookNavIndicator}>
					{`${pendingPostCount}` || '0'}
				</IndicatorCount>
			),
		},
		exportRequest: {
			label: 'Export Requests',
			onClick: () => {
				push(`/${routeRoot}/exports/${childId}`);
			},
			icon: <Schedule />,
		},
		vaultFiles: {
			label: 'Files',
			onClick: () => {
				push(`/${vaultRouteRoot}/files/${childId}`);
			},
			icon: <InsertDriveFile />,
		},
		vaultActivity: {
			label: 'Activity',
			onClick: () => {
				push(`/${vaultRouteRoot}/vaultActivity/${childId}`);
			},
			icon: <List />,
		},
		calendarActivity: {
			label: 'Activity',
			onClick: () => {
				push(`/${calendarActivityRouteRoot}/${childId}`);
			},
			icon: <List />,
		},
		calendar: {
			label: 'Calendar',
			onClick: () => {
				push(`/${calendarRouteRoot}/${childId}`);
			},
			icon: <CalendarMonth />,
		},
		contact: {
			label: 'Contact Us',
			icon: <ContactSupport />,
			onClick: () => {
				push(
					root === 'connect' || pathname.startsWith('/connect/')
						? '/connect/support/contact/'
						: '/support/contact'
				);
			},
		},
		videos: {
			label: 'Tutorials',
			icon: <OndemandVideo />,
			onClick: () => {
				push('/support/videos');
			},
		},
		manual: {
			label: 'Manual',
			icon: <PictureAsPdf />,
			onClick: () => {
				push('/support/manual');
			},
		},
		exit: {
			label: 'Exit',
			onClick: () => {
				localStorage.removeItem('token');
				push('/login/');
			},
			icon: <ExitToApp />,
		},
		exitReadOnly: {
			label: 'Exit',
			onClick: () => {
				const originalToken = localStorage.getItem('originalUser');
				onReadOnlyPasswordConfirm((pass) => {
					setPass(pass);
					setLoader(true);
					getUserFromToken(originalToken).then((e) => {
						getUserWithVerify(e.userId, pass)
							.then(async () => {
								localStorage.removeItem('eroa');
								localStorage.setItem('token', originalToken);
								await initializeUser('login', () => setLoader(false)).then(
									(res) => {
										localStorage.removeItem('originalUser');
										push('/lifebooks/');
									}
								);
							})
							.catch((err) => {
								setLoader(false);
								dispatch(
									setSnackbarReadOnly({
										message: 'Wrong password please try again.',
									})
								);
							});
					});
				});
			},
			icon: <ExitToApp />,
		},
	};

	return navOptions;
}
